/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import classNames from 'classnames';
import React, { CSSProperties, useEffect } from 'react';
import { useTable, useSortBy, usePagination, Row, Cell, Column } from 'react-table';
import { useSticky } from 'react-table-sticky';
import { Button } from 'reactstrap';
import './styles.scss';

interface TableRow<T extends object = any> extends Omit<Row<T>, 'cells'> {
  cells: TableCell<T>[];
}

type TableCell<T extends object = any> = Cell<T> & {
  columns: TableColumn<T>[];
  column: TableColumn<T>;
}

export enum TableVariant {
  Normal = 'normal',
  StickyColumns = 'StickyColumns'
}

export type TableColumn<T extends object = any> = Omit<Column<T>, 'Cell'> & {
  id?: string;
  Header: string | (() => React.ReactNode);
  accessor?: string | ((row: T) => React.ReactNode);
  cellProps?: {
    className?: string;
    style?: CSSProperties;
  };
  resizable?: boolean;
  responsive?: boolean;
  sortable?: boolean;
  disableSortBy?: boolean;
  pageSize?: number;
  sortType?: (a: TableRow<T>, b: TableRow<T>) => number;
  Cell?: (colData: TableCell<T>) => React.ReactNode;
  minWidth?: number;
  sticky?: 'left' | 'right';
};

interface TableProps<T extends object = any> {
  defaultSortIcon?: JSX.Element | null;
  columns: TableColumn<T>[];
  data: T[];
  pageSize?: number;
  className?: string;
  showRowCount?: boolean;
  showHeader?: boolean;
  noData?: string | JSX.Element;
  responsive?: boolean;
  sortBy?: { id: string; desc: boolean }[];
  variant?: TableVariant;
  total?: number;
  unit?: string;
}

const getSortTooltip = (tooltip: string | JSX.Element | undefined) => {
  if (tooltip) {
    return { title: tooltip };
  }
  return {};
} 

const Table = <T extends object = any>(props: TableProps<T>) => {
  const {
    columns,
    data,
    pageSize = 50,
    className,
    showRowCount = false,
    showHeader = true,
    responsive = false,
    noData,
    defaultSortIcon = <i className='fa ml-1 fa-blank' />,
    sortBy = [],
    variant = TableVariant.Normal,
    total,
    unit,
  } = props;

  const [currentPage, setCurrentPage] = React.useState(0);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    // @ts-ignore
    page,
    // @ts-ignore
    canPreviousPage,
    // @ts-ignore
    canNextPage,
    // @ts-ignore
    pageOptions,
    // @ts-ignore
    pageCount,
    // @ts-ignore
    gotoPage,
    // @ts-ignore
    setPageSize,
    // @ts-ignore
    state: { pageIndex },
  } = useTable(
    {
      columns: columns as readonly Column<T>[],
      data,
      disableSortRemove: true,
      initialState: {
        // @ts-ignore
        pageIndex: currentPage,
        pageSize: pageSize,
        sortBy,
      },
    },
    useSortBy,
    usePagination,
    ...(variant === TableVariant.StickyColumns ? [useSticky] : [])
  );

  useEffect(() => {
    // reset current page if the data is out of current view
    if (data.length <= currentPage * pageSize) {
      setCurrentPage(0);
    }
    setPageSize(pageSize);
  }, [data, currentPage, pageSize, setPageSize])

  const changePage = (page: number) => {
    setCurrentPage(page);
    gotoPage(page);
  }

  const sortIndicator = (column: any) => (
    column.isSorted
      ? column.isSortedDesc
        ? <i className='fa ml-1 fa-chevron-up' />
        : <i className='fa ml-1 fa-chevron-down' />
      : defaultSortIcon
  );

  if (data.length === 0 && noData) {
    return (
      <div className={`table-component-container ${className ?? ''}`}>
        {noData}
      </div>
    );
  }

  return (
    <div className={`table-component-container ${responsive === true ? 'table-responsive' : 'table-unresponsive'} ${className ?? ''}`}>
      <div className={classNames('g17-table', {'sticky': variant === TableVariant.StickyColumns})} {...getTableProps()}>
        {showHeader ? (
          <div className='g17-thead'>
            {headerGroups.map(headerGroup => (
              <div className='g17-tr' {...headerGroup.getHeaderGroupProps()}>
              {showRowCount && <div className='g17-tr rowCountCol'></div>}
              {headerGroup.headers.map((column) => (
                <div
                  className={'g17-th'}
                  {...column.getHeaderProps(
                    // @ts-ignore
                    column.getSortByToggleProps({
                      style: { minWidth: column.minWidth, maxWidth: column.maxWidth, width: column.width },
                      // @ts-ignore
                      ...getSortTooltip(column.sortTooltip),
                    })
                  )}
                >
                  <div className='d-flex'>
                    {/* @ts-ignore */}
                    <span className={column.headerClassName ?? ''}>{column.render('Header')}</span>
                    <span className='align-self-center'>{sortIndicator(column)}</span>
                  </div>
                </div>
              ))}
            </div>
            ))}
          </div>
        ) : <></>}
        <div className='g17-tbody' {...getTableBodyProps()}>
          {page.map((row: TableRow<T>, i: number) => {
            prepareRow(row);
            return (
              <div className='g17-tr' {...row.getRowProps()}>
                {showRowCount && <div className='g17-td rowCountCol'>{`${(currentPage * pageSize) + i + 1}.`}</div>}
                {row.cells.map((cell) => {
                  const classes = ['g17-td'];
                  if (cell.column.cellProps?.className) {
                    classes.push(cell.column.cellProps.className);
                  }
                  // if (cell.column.colSpan) {
                  //   classes.push(`w-100`);
                  // }
                  return (
                    <div {...cell.getCellProps([{
                      ...cell.column.cellProps,
                      className: classes.join(' ')
                    }])}
                    >
                      {cell.render('Cell')}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>

      <div className='pagination align-items-center mt-3'>
        {pageCount > 1 && (
          <div>
            <Button size='sm' onClick={() => changePage(0)} disabled={!canPreviousPage}>
              <i className='fa fa-fast-backward' />
            </Button>{' '}
            <Button size='sm' onClick={() => changePage(pageIndex - 1)} disabled={!canPreviousPage}>
              <i className='fa fa-caret-left' />
            </Button>{' '}
            <span>{`Page ${pageIndex + 1} of ${pageOptions.length}`}</span>
            <Button size='sm' onClick={() => changePage(pageIndex + 1)} disabled={!canNextPage}>
              <i className='fa fa-caret-right' />
            </Button>{' '}
            <Button size='sm' onClick={() => changePage(pageCount - 1)} disabled={!canNextPage}>
              <i className='fa fa-fast-forward' />
            </Button>{' '}
          </div>
        )}
        {data.length > 0 && total ? (
          <p className='text-right position-absolute end-0 pr-3 text-xs text-ThemeTextMedium'>
            {data.length} / {total} {unit || ''}
          </p>
        ) : null}
      </div>

    </div>
  )
};
export default Table;
